import { useEffect, useState } from "react";
import clsx from "clsx";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { localNaiveInputToUtcIso, utcToLocalNaive } from "../lib/datetime";

interface DrawerProps {
  open: boolean;
  onClose: () => void;
}

/** UTC ISO → "YYYY-MM-DDTHH:MM" for a datetime-local input, in tz. */
function isoToInput(iso: string | null, tz: string): string {
  if (!iso) return "";
  return utcToLocalNaive(iso, tz)?.slice(0, 16) ?? "";
}

/** Curated IANA zone list for the dropdown. The saved value is prepended
 *  if it isn't already here, so an unusual zone still shows. */
const TIMEZONES = [
  "Europe/Dublin",
  "Europe/London",
  "UTC",
  "Europe/Lisbon",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Madrid",
  "Europe/Athens",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Toronto",
  "Asia/Dubai",
  "Asia/Singapore",
  "Asia/Hong_Kong",
  "Australia/Sydney",
  "Pacific/Auckland",
];

/**
 * Drive setup tool. Two groups: parameters that take effect immediately
 * (name, diameters, length, shift) saved with "Save drive setup"; and
 * re-ingestion parameters (start/end dates) that only matter
 * when re-pulling data, applied together by "Wipe & resync".
 */
export function ResyncDrawer({ open, onClose }: DrawerProps) {
  const qc = useQueryClient();
  const drive = useQuery({ queryKey: ["drive"], queryFn: api.drive });
  const tzForDates = drive.data?.shiftTimezone ?? "Europe/Dublin";

  // Group A — immediate.
  const [name, setName] = useState("");
  const [odMm, setOdMm] = useState("");
  const [idMm, setIdMm] = useState("");
  const [lengthM, setLengthM] = useState("");
  const [tz, setTz] = useState("");
  const [dayStart, setDayStart] = useState("7");
  const [nightStart, setNightStart] = useState("19");
  const [nightEnabled, setNightEnabled] = useState(true);
  const [inclineEnabled, setInclineEnabled] = useState(false);
  const [cutterheadWeight, setCutterheadWeight] = useState("");
  const [tbmWeight, setTbmWeight] = useState("");
  const [tbmLength, setTbmLength] = useState("");
  const [pipeWeightPerM, setPipeWeightPerM] = useState("");
  const [inclineRows, setInclineRows] = useState<
    Array<{ chainage: string; percent: string }>
  >([{ chainage: "0", percent: "" }]);
  const [setupDirty, setSetupDirty] = useState(false);

  // Group B — applied via resync.
  const [startInput, setStartInput] = useState("");
  const [endInput, setEndInput] = useState("");
  const [resyncDirty, setResyncDirty] = useState(false);

  // Seed group A until the operator edits it.
  useEffect(() => {
    if (!drive.data || setupDirty) return;
    const d = drive.data;
    setName(d.name ?? "");
    setOdMm(d.pipeOdMm != null ? String(d.pipeOdMm) : "");
    setIdMm(d.pipeIdMm != null ? String(d.pipeIdMm) : "");
    setLengthM(d.lengthM != null ? String(d.lengthM) : "");
    setTz(d.shiftTimezone ?? "");
    setDayStart(String(d.shift?.dayStartHour ?? 7));
    setNightStart(String(d.shift?.nightStartHour ?? 19));
    setNightEnabled(d.shift?.nightEnabled ?? true);
    const inc = d.incline;
    if (inc) {
      setInclineEnabled(inc.enabled);
      setCutterheadWeight(inc.cutterheadWeightKn ? String(inc.cutterheadWeightKn) : "");
      setTbmWeight(inc.tbmWeightKn ? String(inc.tbmWeightKn) : "");
      setTbmLength(inc.tbmLengthM ? String(inc.tbmLengthM) : "");
      setPipeWeightPerM(inc.pipeWeightPerMKn ? String(inc.pipeWeightPerMKn) : "");
      setInclineRows(
        inc.rows.length > 0
          ? inc.rows.map((r) => ({
              chainage: String(r.chainageM),
              percent: String(r.inclinePercent),
            }))
          : [{ chainage: "0", percent: "" }],
      );
    }
  }, [drive.data, setupDirty]);

  // Seed group B until the operator edits it.
  useEffect(() => {
    if (!drive.data || resyncDirty) return;
    const d = drive.data;
    setStartInput(isoToInput(d.startDate, d.shiftTimezone));
    setEndInput(isoToInput(d.endDate, d.shiftTimezone));
  }, [drive.data, resyncDirty]);

  const setupMutation = useMutation({
    mutationFn: () =>
      api.saveDriveSetup({
        name: name.trim(),
        pipeOdMm: Number(odMm),
        pipeIdMm: Number(idMm),
        lengthM: Number(lengthM),
        shiftTimezone: tz.trim(),
        shiftDayStartHour: Number(dayStart),
        shiftNightStartHour: Number(nightStart),
        shiftNightEnabled: nightEnabled,
        inclineConfig: {
          enabled: inclineEnabled,
          cutterheadWeightKn: Number(cutterheadWeight) || 0,
          tbmWeightKn: Number(tbmWeight) || 0,
          tbmLengthM: Number(tbmLength) || 0,
          pipeWeightPerMKn: Number(pipeWeightPerM) || 0,
          rows: inclineRows
            .map((r) => ({
              chainageM: Number(r.chainage),
              inclinePercent: Number(r.percent),
            }))
            .filter(
              (r) => Number.isFinite(r.chainageM) && Number.isFinite(r.inclinePercent),
            ),
        },
      }),
    onSuccess: (info) => {
      // Write the fresh value straight into the cache so the seed effect
      // re-seeds from it — no flash of the old values before a refetch.
      qc.setQueryData(["drive"], info);
      qc.invalidateQueries({ queryKey: ["latest"] });
      setSetupDirty(false);
    },
  });

  const wipeMutation = useMutation({
    mutationFn: () =>
      api.resyncReadings({
        startDate: localNaiveInputToUtcIso(startInput, tzForDates) ?? undefined,
        endDate: endInput ? localNaiveInputToUtcIso(endInput, tzForDates) : null,
      }),
    onSuccess: () => {
      // Patch the drive cache with the values we just persisted so the
      // group-B seed effect re-reads them (no flash of the old values),
      // then invalidate to re-fetch readings/latest.
      const startIso = localNaiveInputToUtcIso(startInput, tzForDates) ?? null;
      const endIso = endInput ? localNaiveInputToUtcIso(endInput, tzForDates) : null;
      qc.setQueryData(["drive"], (old: typeof drive.data) =>
        old
          ? {
              ...old,
              startDate: startIso ?? old.startDate,
              endDate: endIso,
            }
          : old,
      );
      setResyncDirty(false);
      qc.invalidateQueries();
    },
  });

  const editA = <T,>(setter: (v: T) => void) => (v: T) => { setSetupDirty(true); setter(v); };
  const editB = <T,>(setter: (v: T) => void) => (v: T) => { setResyncDirty(true); setter(v); };

  const tzOptions = tz && !TIMEZONES.includes(tz) ? [tz, ...TIMEZONES] : TIMEZONES;

  return (
    <>
      <div
        className={clsx(
          "fixed inset-0 bg-black/25 transition-opacity z-40",
          open ? "opacity-100" : "opacity-0 pointer-events-none",
        )}
        onClick={onClose}
      />
      <aside
        className={clsx(
          "fixed top-0 right-0 bottom-0 w-[460px] max-lg:w-full max-lg:max-w-none z-50 bg-surface1 border-l border-border1 overflow-y-auto",
          "transform transition-transform duration-200",
          open ? "translate-x-0" : "translate-x-full",
        )}
      >
        <div className="flex items-center justify-between h-16 px-6 border-b border-border1 sticky top-0 bg-surface1 z-10">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-dim">Tools</div>
            <h2 className="text-[15px] font-semibold text-text">Drive setup</h2>
          </div>
          <button onClick={onClose} className="text-muted hover:text-text text-lg">×</button>
        </div>

        <div className="p-6 space-y-7">
          {/* ── Drive parameters ── */}
          <section className="space-y-3">
            <h3 className="text-[12px] font-semibold tracking-[0.16em] uppercase text-text">
              Drive parameters
            </h3>
            <Field label="Drive name">
              <input type="text" value={name}
                onChange={(e) => editA(setName)(e.target.value)} className={inputCls} />
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Pipe OD (mm)">
                <input type="number" step="any" value={odMm}
                  onChange={(e) => editA(setOdMm)(e.target.value)} className={inputCls} />
              </Field>
              <Field label="Pipe ID (mm)">
                <input type="number" step="any" value={idMm}
                  onChange={(e) => editA(setIdMm)(e.target.value)} className={inputCls} />
              </Field>
              <Field label="Drive length (m)">
                <input type="number" step="any" value={lengthM}
                  onChange={(e) => editA(setLengthM)(e.target.value)} className={inputCls} />
              </Field>
            </div>
          </section>

          {/* ── Shift ── */}
          <section className="space-y-3">
            <h3 className="text-[12px] font-semibold tracking-[0.16em] uppercase text-text">
              Shift
            </h3>
            <Field label="Timezone">
              <select value={tz}
                onChange={(e) => editA(setTz)(e.target.value)} className={inputCls}>
                {tzOptions.map((z) => <option key={z} value={z}>{z}</option>)}
              </select>
            </Field>
            <label className="flex items-center gap-2 text-[12px] text-text">
              <input type="checkbox" className="accent-wb" checked={nightEnabled}
                onChange={(e) => editA(setNightEnabled)(e.target.checked)} />
              Night shift
            </label>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Day shift start (24h)">
                <input type="number" min={0} max={23} value={dayStart}
                  onChange={(e) => editA(setDayStart)(e.target.value)} className={inputCls} />
              </Field>
              {nightEnabled && (
                <Field label="Night shift start (24h)">
                  <input type="number" min={0} max={23} value={nightStart}
                    onChange={(e) => editA(setNightStart)(e.target.value)} className={inputCls} />
                </Field>
              )}
            </div>
          </section>

          {/* ── Incline ── */}
          <section className="space-y-3">
            <h3 className="text-[12px] font-semibold tracking-[0.16em] uppercase text-text">
              Incline
            </h3>
            <p className="text-[11.5px] text-muted leading-snug">
              Horizontal weight component. Positive when pushing uphill
              (adds to the jacking load); negative when pushing downhill
              (assists).
            </p>
            <label className="flex items-center gap-2 text-[12px] text-text">
              <input type="checkbox" className="accent-wb" checked={inclineEnabled}
                onChange={(e) => editA(setInclineEnabled)(e.target.checked)} />
              Include resolved weight
            </label>
            {inclineEnabled && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Cutterhead weight (kN)">
                    <input type="number" step="any" value={cutterheadWeight}
                      onChange={(e) => editA(setCutterheadWeight)(e.target.value)} className={inputCls} />
                  </Field>
                  <Field label="TBM weight (kN)">
                    <input type="number" step="any" value={tbmWeight}
                      onChange={(e) => editA(setTbmWeight)(e.target.value)} className={inputCls} />
                  </Field>
                  <Field label="TBM length (m)">
                    <input type="number" step="any" value={tbmLength}
                      onChange={(e) => editA(setTbmLength)(e.target.value)} className={inputCls} />
                  </Field>
                  <Field label="Pipe weight (kN/m)">
                    <input type="number" step="any" value={pipeWeightPerM}
                      onChange={(e) => editA(setPipeWeightPerM)(e.target.value)} className={inputCls} />
                  </Field>
                </div>
                <div>
                  <div className="grid grid-cols-[1fr_1fr_2rem] gap-2 text-[10.5px] uppercase tracking-wider text-dim mb-1">
                    <span>Chainage start (m)</span>
                    <span>Incline (%)</span>
                    <span />
                  </div>
                  {inclineRows.map((row, idx) => (
                    <div key={idx} className="grid grid-cols-[1fr_1fr_2rem] gap-2 items-center mb-1">
                      <input
                        type="number"
                        step="any"
                        value={row.chainage}
                        readOnly={idx === 0}
                        onChange={(e) =>
                          editA(setInclineRows)(
                            inclineRows.map((r, i) =>
                              i === idx ? { ...r, chainage: e.target.value } : r,
                            ),
                          )
                        }
                        className={
                          idx === 0
                            ? inputCls + " bg-surface3 text-muted cursor-not-allowed"
                            : inputCls
                        }
                      />
                      <input
                        type="number"
                        step="any"
                        value={row.percent}
                        onChange={(e) =>
                          editA(setInclineRows)(
                            inclineRows.map((r, i) =>
                              i === idx ? { ...r, percent: e.target.value } : r,
                            ),
                          )
                        }
                        className={inputCls}
                      />
                      <button
                        type="button"
                        disabled={idx === 0}
                        onClick={() =>
                          editA(setInclineRows)(
                            inclineRows.filter((_, i) => i !== idx),
                          )
                        }
                        className="text-muted hover:text-wb disabled:opacity-30 disabled:hover:text-muted text-lg leading-none"
                        title={idx === 0 ? "Row 0 must remain (drive start)" : "Remove row"}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() =>
                      editA(setInclineRows)([
                        ...inclineRows,
                        { chainage: "", percent: "" },
                      ])
                    }
                    className="text-[11px] uppercase tracking-wider text-muted hover:text-text mt-1"
                  >
                    + add row
                  </button>
                </div>
              </>
            )}
          </section>

          <div className="flex items-center gap-3 border-t border-border1 pt-4">
            <button
              onClick={() => setupMutation.mutate()}
              disabled={setupMutation.isPending}
              className={clsx(
                "rounded px-4 py-2 text-[11px] uppercase tracking-widest transition-colors",
                setupMutation.isPending
                  ? "bg-border1 text-dim cursor-not-allowed"
                  : "bg-wb hover:bg-wbHot text-white",
              )}
            >
              {setupMutation.isPending ? "Saving…" : "Save drive setup"}
            </button>
            {setupMutation.isSuccess && !setupDirty && (
              <span className="text-[11px] text-muted">Saved.</span>
            )}
            {setupMutation.isError && (
              <span className="text-[11px] text-wb">{(setupMutation.error as Error).message}</span>
            )}
          </div>

          {/* ── Data resync (start/end + wipe) ── */}
          <section className="border-t border-border1 pt-6 space-y-3">
            <h3 className="text-[12px] font-semibold tracking-[0.16em] uppercase text-text">
              Data resync
            </h3>
            <p className="text-[11.5px] text-muted leading-snug">
              Wipes the local readings tables and resets cursors. Workers
              re-pull everything from drive start.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Start date">
                <input type="datetime-local" value={startInput}
                  onChange={(e) => editB(setStartInput)(e.target.value)} className={inputCls} />
              </Field>
              <Field label="End date (optional)">
                <input type="datetime-local" value={endInput}
                  onChange={(e) => editB(setEndInput)(e.target.value)} className={inputCls} />
              </Field>
            </div>
            <button
              onClick={() => wipeMutation.mutate()}
              disabled={wipeMutation.isPending}
              className={clsx(
                "w-full rounded px-3 py-2.5 text-[12px] uppercase tracking-widest transition-colors mt-1",
                wipeMutation.isPending
                  ? "bg-border1 text-dim cursor-not-allowed"
                  : "bg-wb hover:bg-wbHot text-white",
              )}
            >
              {wipeMutation.isPending ? "Wiping…" : "Wipe & resync"}
            </button>
            {wipeMutation.isSuccess && (
              <p className="text-[12px] text-text">Wiped. Re-ingest in progress.</p>
            )}
            {wipeMutation.isError && (
              <p className="text-[12px] text-wb">{(wipeMutation.error as Error).message}</p>
            )}
          </section>
        </div>
      </aside>
    </>
  );
}

const inputCls =
  "w-full bg-surface2 border border-border1 rounded px-2 py-2 text-text text-[13px] focus:border-wb/60 outline-none";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10.5px] uppercase tracking-wider text-dim">{label}</span>
      {children}
    </label>
  );
}
